import { Event, IEvent } from '../event/event'

export interface IAnalyticsEvent extends IEvent {
    setMetaProperties(metaProperties: any): void
    getProperties(): any
}

interface IVendorControlFields {
    keys?: { [key: string]: string }
    extra_keys?: string[]
    exclude_common_keys?: string[]
}
export interface IMParticleControlFields extends IVendorControlFields {
    mp_event_type?: string
    mp_native_event?: boolean
    mp_custom_flags?: string[]
}
export interface IKruxControlFields extends IVendorControlFields {
    krx_event_id?: string
    krx_event_type?: string
}
export interface IGTMControlFields extends IVendorControlFields {
    gtm_event_type?: string
}
interface IControlFields {
    mparticle?: IMParticleControlFields
    krux?: IKruxControlFields
    gtm?: IGTMControlFields
    permutive?: IVendorControlFields
    extra_keys?: string[]
}
interface IEventData {
    [key: string]: string|IControlFields
    control_fields: IControlFields
}

type Iprovider = 'mparticle' | 'krux' | 'gtm' | 'permutive'

export class AnalyticsEvent extends Event implements IAnalyticsEvent {
    protected eventData: IEventData
    protected metaProperties: any

    /**
     * Set meta properties for use in any extended event
     *
     * @param metaProperties
     */
    public setMetaProperties(metaProperties: any): void {
        this.metaProperties = metaProperties
    }

    /**
     * Get the base properties for an event, safely parsing the value
     */
    public getProperties(): any {

        if (this.value && this.value.length && JSON.parse(this.value)) {
            return JSON.parse(this.value)
        }

        return undefined
    }

    /**
     *  Get event attributes List (extra keys)
     *
     * @returns string[]
     */
    public getGeneralAttributesList(): string[] {
        // Get Dom Event Data
        this.eventData = this.eventData || this.getProperties()

        return this.eventData && this.eventData.control_fields &&
        this.eventData.control_fields.extra_keys ? this.eventData.control_fields.extra_keys : []
    }

    /**
     * Get Provider specific attribute list by combining the provider
     * common attributes and provider extra keys
     *
     * @param provider
     * @returns string[]
     */
    public getProviderAttributesList(provider: Iprovider = 'mparticle', commonAttrs: string[]=[]): string[] {
        let attrs: string [] = []
        // Get Dom Event Data
        this.eventData = this.eventData || this.getProperties()

        if(this.eventData && this.eventData.control_fields && this.eventData.control_fields[provider]) {
            // Exclude Keys from Common Attributes
            const excludeCommonKeys = this.eventData.control_fields[provider].exclude_common_keys || []
            attrs = commonAttrs.filter((attr) => !excludeCommonKeys.includes(attr))

            // Add extra keys
            if(this.eventData.control_fields[provider].extra_keys) {
                attrs = [...attrs ,...this.eventData.control_fields[provider].extra_keys ]
            }
        } else {
            attrs = commonAttrs
        }
        return [... new Set(attrs)] // remove duplicates
    }

    /**
     * Get Provider specific control fields data
     *
     * @param provider
     * @returns { [key: string]: string }
     */
    public getProviderControlField(provider: Iprovider = 'mparticle'): IVendorControlFields {
        // Get Dom Event Data
        this.eventData = this.eventData || this.getProperties()

        if(this.eventData.control_fields && this.eventData.control_fields[provider]) {
            return this.eventData.control_fields[provider]
        }

        return {}
    }

    /**
     *  Get Custom Attributes from event data element
     *
     * @return { [key: string]: string }
     */
    public getProviderCustomData(provider: Iprovider = 'mparticle'): { [key: string]: string } {

        const providerCustomData: { [key: string]: string } = {}

        // Get Dom Event Data
        const { control_fields, ...customData } = this.eventData || this.getProperties()

        if (control_fields[provider] && control_fields[provider].keys) {
            const providerKeys: { [key: string]: string } = control_fields[provider].keys

            Object.keys(providerKeys).forEach((attrKey) => {
                providerCustomData[providerKeys[attrKey]] = customData[attrKey]
            })
        }

        return providerCustomData
    }

    /**
     * Get control field
     */
    protected getControlFields(): IControlFields {
        return this.eventData && this.eventData.control_fields? this.eventData.control_fields:{}
    }
}
